<template>
  <div class="auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <img src="/lamusica-logo.svg" alt="LaMusica" class="auth-logo">
        <p>Create new password</p>
      </div>
      
      <div v-if="loading" class="loading-state">
        <p>Verifying reset link...</p>
      </div>
      
      <div v-else-if="invalidToken" class="error-state">
        <div class="error-icon">
          <AppIcon name="exclamation-circle" />
        </div>
        <h3>Invalid or Expired Link</h3>
        <p>This password reset link is no longer valid. It may have expired or already been used.</p>
        <router-link to="/forgot-password" class="btn-primary">Request New Link</router-link>
      </div>
      
      <div v-else-if="success" class="success-state">
        <div class="success-icon">
          <AppIcon name="check-circle" />
        </div>
        <h3>Password Reset Complete</h3>
        <p>Your password has been successfully updated. You can now sign in with your new password.</p>
        <router-link to="/login" class="btn-primary">Sign In</router-link>
      </div>
      
      <form v-else @submit.prevent="handleSubmit" class="auth-form">
        <div class="form-group">
          <label for="password">New Password</label>
          <input
            id="password"
            v-model="password"
            type="password"
            placeholder="••••••••"
            required
            autofocus
            minlength="8"
          />
          <span class="hint">Must be at least 8 characters</span>
        </div>
        
        <div class="form-group">
          <label for="confirmPassword">Confirm Password</label>
          <input
            id="confirmPassword"
            v-model="confirmPassword"
            type="password"
            placeholder="••••••••"
            required
            minlength="8"
          />
        </div>
        
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        
        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? 'Resetting...' : 'Reset Password' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResetPasswordView',
  data() {
    return {
      password: '',
      confirmPassword: '',
      error: null,
      loading: true,
      submitting: false,
      invalidToken: false,
      success: false
    }
  },
  computed: {
    token() {
      return this.$route.params.token
    }
  },
  async mounted() {
    await this.verifyToken()
  },
  methods: {
    async verifyToken() {
      try {
        const res = await fetch(`/api/auth/reset-password/${this.token}`)
        const data = await res.json()
        
        if (!data.valid) {
          this.invalidToken = true
        }
      } catch (err) {
        this.invalidToken = true
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      this.error = null
      
      if (this.password !== this.confirmPassword) {
        this.error = 'Passwords do not match'
        return
      }
      
      if (this.password.length < 8) {
        this.error = 'Password must be at least 8 characters'
        return
      }
      
      this.submitting = true
      
      try {
        const res = await fetch('/api/auth/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            token: this.token,
            password: this.password 
          })
        })
        
        const data = await res.json()
        
        if (!res.ok) {
          throw new Error(data.error || 'Failed to reset password')
        }
        
        this.success = true
      } catch (err) {
        this.error = err.message
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gray-100);
  padding: 20px;
}

.auth-card {
  background: var(--white);
  border-radius: 8px;
  box-shadow: var(--shadow-xl);
  padding: 40px;
  width: 100%;
  max-width: 400px;
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-logo {
  height: 40px;
  width: auto;
  margin-bottom: 12px;
}

.auth-header p {
  font-size: 14px;
  color: var(--gray-500);
  margin: 0;
}

.loading-state {
  text-align: center;
  padding: 20px 0;
  color: var(--gray-500);
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-700);
}

.form-group input {
  padding: 10px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(51, 147, 255, 0.1);
}

.form-group .hint {
  font-size: 12px;
  color: var(--gray-500);
}

.error-message {
  background: #fef2f2;
  color: var(--error);
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
}

.btn-primary {
  display: inline-block;
  padding: 12px;
  background: var(--accent);
  color: var(--white);
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-state,
.success-state {
  text-align: center;
}

.error-icon i {
  font-size: 48px;
  color: var(--error);
  margin-bottom: 16px;
}

.success-icon i {
  font-size: 48px;
  color: var(--success);
  margin-bottom: 16px;
}

.error-state h3,
.success-state h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 12px 0;
}

.error-state p,
.success-state p {
  font-size: 14px;
  color: var(--gray-600);
  margin: 0 0 24px 0;
  line-height: 1.5;
}
</style>
